import React from 'react';

const features = [
  {
    hex: require('../assets/img/advantage/hex-fast.svg').default,
    icon: require('../assets/img/advantage/icon-fast.svg').default,
    iconClass: 'w-[24px] h-[33px] lg:w-[48px] lg:h-[66px]',
    title: 'Fast',
    subtitle: 'Development Speed',
    desc: 'Accelerated delivery',
  },
  {
    hex: require('../assets/img/advantage/hex-full.svg').default,
    icon: require('../assets/img/advantage/icon-full.svg').default,
    iconClass: 'w-[22px] h-[27px] lg:w-[44px] lg:h-[54px]',
    title: 'Full',
    subtitle: 'Documentation Coverage',
    desc: 'Complete transparency',
  },
  {
    hex: require('../assets/img/advantage/hex-high.svg').default,
    icon: require('../assets/img/advantage/icon-high.svg').default,
    iconClass: 'w-[24px] h-[24px] lg:w-[48px] lg:h-[48px]',
    title: 'High',
    subtitle: 'First-Pass Accuracy',
    desc: 'Minimal revisions needed',
  },
  {
    hex: require('../assets/img/advantage/hex-quick.svg').default,
    icon: require('../assets/img/advantage/icon-quick.svg').default,
    iconClass: 'w-[37px] h-[27px] lg:w-[74px] lg:h-[54px]',
    title: 'Quick',
    subtitle: 'Time to Market',
    desc: 'Launch sooner',
  },
];

const AdvantageSection: React.FC = () => {
  return (
    <section className="py-12 lg:py-20 bg-white">
      <div className="max-w-content mx-auto px-6">
        <h2 className="font-exo font-bold text-ebony-clay text-2xl lg:text-[38px] tracking-[1.9px] leading-normal text-center mb-4">
          The Metalogix Advantage
        </h2>
        <p className="font-proxima text-black text-sm lg:text-base tracking-[0.8px] leading-normal text-center mb-10 lg:mb-12">
          Traditional vs. Document Centric AI Development&trade;
        </p>

        {/* Feature cards grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-0 mb-10 lg:mb-12">
          {features.map((f) => (
            <div key={f.title} className="p-4 lg:p-6 flex flex-col items-center text-center">
              <div className="relative w-[88px] lg:w-[158px] h-[88px] lg:h-[158px] mb-4">
                <img className="w-full h-full" src={f.hex} alt="" />
                <img className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 ${f.iconClass}`} src={f.icon} alt="" />
              </div>
              <div className="font-exo font-semibold text-eerie-black text-xl lg:text-[32px] tracking-[1.6px] leading-normal">
                {f.title}
              </div>
              <div className="font-exo font-semibold text-eerie-black text-xs lg:text-base tracking-[0.8px] leading-normal">
                {f.subtitle}
              </div>
              <div className="font-proxima text-black text-xs lg:text-base tracking-[0.8px] leading-normal mt-1">
                {f.desc}
              </div>
            </div>
          ))}
        </div>

        {/* Comparison bars */}
        <div className="flex flex-col md:flex-row">
          {/* Traditional Development */}
          <div className="flex-1 bg-[#26283f] rounded-t-lg md:rounded-l-lg md:rounded-tr-none p-6 lg:p-8 text-center">
            <h3 className="font-exo font-bold text-white text-xl lg:text-[32px] tracking-[1.6px] leading-normal">
              Traditional Development
            </h3>
            <div className="font-exo font-bold text-[#ff686a] text-xl lg:text-[28px] tracking-[1.4px] leading-normal italic mt-2">
              slow
            </div>
            <div className="font-exo font-bold text-white text-3xl lg:text-[40px] tracking-[2px] leading-normal my-4">
              6 Months
            </div>
            <p className="font-proxima-semibold text-white text-xs lg:text-base tracking-[0.8px] leading-normal">
              Requirements &rarr; Design &rarr; Development &rarr; Testing &rarr; Deployment
            </p>
          </div>

          {/* Metalogix Approach */}
          <div className="flex-1 bg-royal-blue rounded-b-lg md:rounded-r-lg md:rounded-bl-none p-6 lg:p-8 text-center relative overflow-hidden">
            <img
              className="absolute inset-0 w-full h-full object-cover"
              src={require('../assets/img/advantage/process-bg.svg').default}
              alt=""
            />
            {/* Logo watermark — hidden on mobile */}
            <div className="absolute left-1/2 -translate-x-1/2 top-4 w-[200px] lg:w-[274px] h-[190px] lg:h-[257px] opacity-[0.15] hidden lg:block pointer-events-none">
              <img className="absolute left-0 top-[20.44%] w-[18.89%] h-[35.07%]" src={require('../assets/img/advantage/logo-1.svg').default} alt="" />
              <img className="absolute left-[19.45%] top-0 w-[61.13%] h-[49.67%]" src={require('../assets/img/advantage/logo-2.svg').default} alt="" />
              <img className="absolute left-[44.89%] top-[35.35%] w-[55.11%] h-[51.28%]" src={require('../assets/img/advantage/logo-3.svg').default} alt="" />
              <img className="absolute left-[44.7%] top-[78.78%] w-[36.12%] h-[21.22%]" src={require('../assets/img/advantage/logo-4.svg').default} alt="" />
              <img className="absolute left-[60.51%] top-0 w-[32.14%] h-[33.17%]" src={require('../assets/img/advantage/logo-5.svg').default} alt="" />
              <img className="absolute left-[10.72%] top-[31.95%] w-[37.39%] h-[54.58%]" src={require('../assets/img/advantage/logo-6.svg').default} alt="" />
            </div>
            <div className="relative z-10">
              <h3 className="font-exo font-bold text-white text-xl lg:text-[32px] tracking-[1.6px] leading-normal" style={{ textShadow: '0px 4px 4px #00000040' }}>
                Metalogix Approach
              </h3>
              <div className="font-exo font-bold text-[#fcba3f] text-xl lg:text-[28px] tracking-[1.4px] leading-normal italic mt-2">
                fast
              </div>
              <div className="font-exo font-bold text-white text-3xl lg:text-[40px] tracking-[2px] leading-normal my-4">
                6 Weeks
              </div>
              <p className="font-proxima-semibold text-white text-xs lg:text-base tracking-[0.8px] leading-normal">
                Documentation &rarr; AI Generation &rarr; Review &rarr; Delivery
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdvantageSection;
